import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, Download } from 'lucide-react';
import { api, hlaskaZChyby } from '../api/client.js';
import type { DenikZaznam, DenikZaznamDetail, Zakazka } from '../api/types.js';
import {
  Badge,
  Chyba,
  EmptyState,
  Input,
  Karta,
  Nacitani,
  SkeletonSeznam,
} from '../components/ui.js';
import { formatDen } from '../lib/format.js';
import { useVybranaZakazka } from '../lib/globalniZakazka.js';
import { HledaniDeniku } from './teren/HledaniDeniku.js';
import { PodpisNahled } from '../components/PodpisNahled.js';
import { KalendarDeniku } from '../components/KalendarDeniku.js';
import { denVTydnu } from './teren/datum.js';
import { VYZVA_STAV_BARVA, VYZVA_STAV_LABEL } from '../lib/labels.js';

/**
 * Kancelářské prohlížení deníku (read-only) jako ČTEČKA (split-view): dny
 * vlevo (datum, kdo zapsal, stav podpisu), obsah vybraného dne vpravo.
 * Fulltext a exporty PDF/XLSX za období (checklist F1–F2) v levém sloupci.
 * Zápis patří do terénu — kancelář deník čte a exportuje.
 */
export function DenikPrehledPage() {
  const [od, setOd] = useState('');
  const [doDatum, setDoDatum] = useState('');
  const [vybranyId, setVybranyId] = useState<string | null>(null);

  const { data: zakazky, isLoading } = useQuery({
    queryKey: ['zakazky'],
    queryFn: () => api.get<Zakazka[]>('/api/projects'),
  });

  // Zakázka z globálního přepínače v horní liště (jeden kontext).
  const { vybrana } = useVybranaZakazka(zakazky);

  const { data: dny, error } = useQuery({
    queryKey: ['denik-dny', vybrana?.id],
    queryFn: () =>
      api.get<DenikZaznam[]>(`/api/diary?zakazkaId=${encodeURIComponent(vybrana?.id ?? '')}`),
    enabled: !!vybrana,
  });

  if (isLoading) return <Nacitani />;
  if (!zakazky?.length) return <p className="text-text-secondary">Zatím žádné zakázky.</p>;

  const obdobi = new URLSearchParams();
  if (od) obdobi.set('od', od);
  if (doDatum) obdobi.set('do', doDatum);
  const suffix = obdobi.toString() ? `?${obdobi.toString()}` : '';

  // Vybraný den: držíme volbu, dokud existuje; default nejnovější (první).
  const aktivniId =
    vybranyId && dny?.some((d) => d.id === vybranyId) ? vybranyId : (dny?.[0]?.id ?? null);

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-text-heading">Deník</h1>
          <p className="text-sm text-text-secondary">
            Čtení a export — zápis dělá terén.
            {vybrana ? ` · ${vybrana.cislo} — ${vybrana.nazev}` : ''}
          </p>
        </div>
        {/* Toolbar exportu: období + PDF/XLSX v jednom řádku (ne velká karta). */}
        {vybrana && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="w-38">
              <Input
                type="date"
                aria-label="Export od"
                className="!min-h-10 !text-sm"
                value={od}
                onChange={(e) => setOd(e.target.value)}
              />
            </span>
            <span aria-hidden="true" className="text-text-secondary">
              –
            </span>
            <span className="w-38">
              <Input
                type="date"
                aria-label="Export do"
                className="!min-h-10 !text-sm"
                value={doDatum}
                onChange={(e) => setDoDatum(e.target.value)}
              />
            </span>
            <a
              className="inline-flex min-h-10 items-center gap-2 rounded-lg bg-primary px-3.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover"
              href={`/api/diary/${vybrana.id}/export.pdf${suffix}`}
            >
              <Download size={16} strokeWidth={2} aria-hidden="true" />
              PDF
            </a>
            <a
              className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-border bg-surface px-3.5 text-sm font-semibold shadow-sm hover:bg-bg"
              href={`/api/diary/${vybrana.id}/export.xlsx${suffix}`}
            >
              <Download size={16} strokeWidth={2} aria-hidden="true" />
              Excel
            </a>
          </div>
        )}
      </div>

      {error ? <Chyba>{hlaskaZChyby(error)}</Chyba> : null}
      {!dny && vybrana && <SkeletonSeznam radku={4} />}
      {dny && dny.length === 0 && (
        <EmptyState
          ikona={<BookOpen size={22} strokeWidth={1.75} />}
          titulek="Zatím žádné záznamy"
          popis="Deník plní terén — dny se tu objeví, jakmile mistr začne zapisovat."
        />
      )}

      {/* Čtečka: dny + hledání vlevo, obsah vybraného dne vpravo. */}
      {dny && dny.length > 0 && (
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(260px,320px)_minmax(0,1fr)]">
          <div className="flex flex-col gap-3">
            {/* Kalendář úplnosti — podepsáno/nepodepsáno/DÍRY na jeden pohled. */}
            <KalendarDeniku dny={dny} vybranyId={aktivniId} onVybrat={setVybranyId} />
            <div className="flex flex-col gap-1.5">
              {dny.map((d) => (
                <DenRadek
                  key={d.id}
                  den={d}
                  vybrany={d.id === aktivniId}
                  onVybrat={() => setVybranyId(d.id)}
                />
              ))}
            </div>
            {vybrana && (
              <HledaniDeniku zakazkaId={vybrana.id} cestaZaznamu={(id) => `/denik/${id}`} />
            )}
          </div>
          {aktivniId && <DenikDenObsah id={aktivniId} />}
        </div>
      )}
    </>
  );
}

/** Řádek dne v čtečce: den v týdnu + datum, kdo zapsal, stav podpisu, typ. */
function DenRadek({
  den,
  vybrany,
  onVybrat,
}: {
  den: DenikZaznam;
  vybrany: boolean;
  onVybrat: () => void;
}) {
  return (
    <button
      type="button"
      aria-current={vybrany}
      onClick={onVybrat}
      className={`flex w-full items-center justify-between gap-2 rounded-lg border p-3 text-left transition-colors duration-100 ${
        vybrany
          ? 'border-primary bg-primary-tint shadow-sm'
          : 'border-border bg-surface hover:border-border-strong hover:bg-bg'
      }`}
    >
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-text-heading">
          {denVTydnu(den.datum)} {formatDen(den.datum)}
        </span>
        <span className="block truncate text-xs text-text-secondary">
          {den.zapsal?.jmeno ?? '—'}
          {den.typ === 'montazni' ? ` · montážní — ${den.vlastnikSubdodavatel?.nazev}` : ''}
          {den.opravujeZaznamId ? ' · opravný' : ''}
        </span>
      </span>
      {den.nepodepsany ? (
        <Badge stav="warning">Nepodepsáno</Badge>
      ) : (
        <Badge stav="success">Podepsáno</Badge>
      )}
    </button>
  );
}

/**
 * Obsah jednoho dne (read-only) — sdílený čtečkou (/denik) i samostatnou
 * routou (/denik/:id, deep-linky z Přehledu a hledání).
 */
function DenikDenObsah({ id }: { id: string }) {
  const {
    data: z,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['denik-zaznam', id],
    queryFn: () => api.get<DenikZaznamDetail>(`/api/diary/${id}`),
    enabled: !!id,
  });

  if (isLoading) return <SkeletonSeznam radku={2} vyska="h-40" />;
  if (error) return <Chyba>{hlaskaZChyby(error)}</Chyba>;
  if (!z) return null;

  return (
    <div className="flex flex-col gap-4">
      {/* Hlavička dne: datum + stav podpisu (razítko dokumentu). */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-bold text-text-heading">
          {denVTydnu(z.datum)} {formatDen(z.datum)}
        </h2>
        {z.nepodepsany ? (
          <Badge stav="warning">Nepodepsáno</Badge>
        ) : (
          <Badge stav="success">Podepsáno{z.podepsal ? ` — ${z.podepsal.jmeno}` : ''}</Badge>
        )}
      </div>

      {/* Vlastnoruční podpis dne (razítko dokumentu). */}
      {(z.podpisy ?? []).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {(z.podpisy ?? []).map((pod) => (
            <PodpisNahled key={pod.id} podpis={pod} />
          ))}
        </div>
      )}

      {z.priznakViceprace ? (
        <p className="rounded-lg bg-warning-bg px-3 py-2 text-sm font-medium text-warning-text">
          ⚠ V tomto dni byla ohlášena vícepráce (nález).
        </p>
      ) : null}

      {/* Hlavní obsah dne: práce dominantně, zbytek jako fakty. */}
      <Karta>
        <p className="text-xs font-bold tracking-wide text-text-secondary uppercase">
          Provedené práce
        </p>
        <p className="mt-1 text-base whitespace-pre-wrap text-text-primary">
          {z.provedenePrace ?? '—'}
        </p>
        <dl className="mt-5 grid gap-x-8 gap-y-4 border-t border-border pt-4 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-bold tracking-wide text-text-secondary uppercase">
              Lidé na stavbě
            </dt>
            <dd className="mt-0.5 text-sm">
              {z.lideNaStavbe?.length
                ? z.lideNaStavbe.map((l) => `${l.nazev}: ${l.pocet}`).join(' · ')
                : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-bold tracking-wide text-text-secondary uppercase">
              Počasí
            </dt>
            <dd className="mt-0.5 text-sm">{z.pocasi ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-bold tracking-wide text-text-secondary uppercase">
              Materiál
            </dt>
            <dd className="mt-0.5 text-sm whitespace-pre-wrap">{z.material ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-bold tracking-wide text-text-secondary uppercase">
              Mechanizace
            </dt>
            <dd className="mt-0.5 text-sm whitespace-pre-wrap">{z.mechanizace ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-bold tracking-wide text-text-secondary uppercase">
              Zapsal
            </dt>
            <dd className="mt-0.5 text-sm">{z.zapsal?.jmeno ?? '—'}</dd>
          </div>
        </dl>
      </Karta>

      {z.prilohy.length > 0 && (
        <Karta nadpis={`Fotky (${z.prilohy.length})`}>
          <div className="flex flex-wrap gap-3">
            {z.prilohy.map((p) => (
              <figure key={p.id} className="w-32">
                <img
                  src={`/api/diary/prilohy/${p.id}/soubor`}
                  alt={p.popis ?? p.nazev}
                  className="h-32 w-32 rounded-lg border border-border object-cover"
                  loading="lazy"
                />
                <figcaption className="text-sm text-text-secondary">
                  {p.popis ?? p.nazev}
                  {p.bezGps ? ' · bez GPS' : ''}
                </figcaption>
              </figure>
            ))}
          </div>
        </Karta>
      )}

      {z.vyzvy.length > 0 && (
        <Karta nadpis="Výzvy k záznamu">
          <ul className="flex flex-col gap-2">
            {z.vyzvy.map((v) => (
              <li key={v.id} className="flex items-start justify-between gap-2 text-base">
                <span>
                  {v.adresatUser?.jmeno ?? v.adresatRole} — {v.text}
                </span>
                <Badge barva={VYZVA_STAV_BARVA[v.stav]}>{VYZVA_STAV_LABEL[v.stav]}</Badge>
              </li>
            ))}
          </ul>
        </Karta>
      )}
    </div>
  );
}

/** Samostatný read-only detail dne (deep-linky z Přehledu, hledání, nálezů). */
export function DenikZaznamKancelarPage() {
  const { id = '' } = useParams();

  return (
    <>
      <div className="text-sm text-text-secondary">
        <Link to="/denik" className="underline underline-offset-2">
          Deník
        </Link>{' '}
        / záznam
      </div>
      <DenikDenObsah id={id} />
    </>
  );
}
